import React, {useState, useEffect, useRef} from 'react'
import {
    Box,
    Button,
    Select,
    VStack,
    HStack,
    Text,
    Portal,
    useToast
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    storePreviewData,
    clearPreviewData,
    getLanguageCode,
    getPreviewSession,
    syncPreviewSession
} from '../../utils/optimizely-preview'

// Screen corners the widget can snap to, ordered clockwise so the rotate
// arrows step through them predictably. Persisted per session.
const CORNERS = ['top-left', 'top-right', 'bottom-right', 'bottom-left']
const CORNER_STORAGE_KEY = 'optimizely_preview_corner'
const EDGE = '16px'
const BOTTOM_EDGE = '80px' // clears the Clara chat button so both remain accessible
const TOP_EDGE = '90px' // clears the sticky header so top corners stay usable

const cornerPosition = (corner) => {
    switch (corner) {
        case 'top-left':
            return {top: TOP_EDGE, left: EDGE}
        case 'top-right':
            return {top: TOP_EDGE, right: EDGE}
        case 'bottom-left':
            return {bottom: BOTTOM_EDGE, left: EDGE}
        case 'bottom-right':
        default:
            return {bottom: BOTTOM_EDGE, right: EDGE}
    }
}

// The clockwise-next corner from each position, used to label the move control.
const NEXT_DIRECTION = ['right', 'down', 'left', 'up']

/**
 * Four-way move cross with only the next-move arrow highlighted. The base
 * highlight points right; rotating the whole icon by cornerIndex × 90° walks
 * that highlight clockwise (right → down → left → up) in step with the corner
 * cycle, so a click both moves the widget and spins the arrow to its new side.
 */
const MoveCornerIcon = ({corner, size = 18}) => {
    const angle = Math.max(0, CORNERS.indexOf(corner)) * 90
    return (
        <Box
            as="svg"
            viewBox="0 0 24 24"
            width={`${size}px`}
            height={`${size}px`}
            aria-hidden="true"
            sx={{
                transform: `rotate(${angle}deg)`,
                transformOrigin: 'center',
                transition: 'transform 0.25s ease'
            }}
        >
            {/* Dimmed arrows: up, down, left */}
            <g fill="#CBD5E0">
                <polygon points="12,2 8,6 16,6" />
                <rect x="10.5" y="6" width="3" height="5" />
                <polygon points="12,22 8,18 16,18" />
                <rect x="10.5" y="13" width="3" height="5" />
                <polygon points="2,12 6,8 6,16" />
                <rect x="6" y="10.5" width="5" height="3" />
            </g>
            {/* Highlighted arrow: right = the next move */}
            <g fill="#3182CE">
                <polygon points="22,12 18,8 18,16" />
                <rect x="13" y="10.5" width="5" height="3" />
            </g>
        </Box>
    )
}

const PreviewModeProjectSelector = () => {
    const [isPreviewMode, setIsPreviewMode] = useState(false)
    const [selectedProject, setSelectedProject] = useState('')
    const [availableProjects, setAvailableProjects] = useState([])
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isApplying, setIsApplying] = useState(false)
    const [error, setError] = useState(null)
    const [corner, setCorner] = useState(() => {
        if (typeof window === 'undefined') return 'bottom-right'
        try {
            return sessionStorage.getItem(CORNER_STORAGE_KEY) || 'bottom-right'
        } catch {
            return 'bottom-right'
        }
    })
    const widgetRef = useRef(null)
    const toast = useToast()
    const {locale, site} = useMultiSite()

    // The CMS draft-projects config is a per-site preference, so the proxy must
    // know which site to resolve it for. Pass the active siteId as a query param
    // (getSiteSpecificEnvVarValue reads req.query.siteId first).
    const siteQuery = site?.id ? `?siteId=${encodeURIComponent(site.id)}` : ''

    // Function to get current locale from multisite context
    const getCurrentLocale = () => {
        // Use the locale from multisite context, fallback to en-GB if not available
        return locale?.id || locale?.alias || 'en-GB'
    }


    // Step the widget to an adjacent corner (dir: +1 clockwise, -1 counter-clockwise)
    const moveCorner = (dir) => {
        setCorner((prev) => {
            const i = CORNERS.indexOf(prev)
            const next = CORNERS[(i + dir + CORNERS.length) % CORNERS.length]
            try {
                sessionStorage.setItem(CORNER_STORAGE_KEY, next)
            } catch {
                // sessionStorage unavailable — keep the in-memory value only
            }
            return next
        })
    }

    useEffect(() => {
        const checkPreviewMode = () => {
            if (typeof window === 'undefined') return false

            // Manual override via console: window.__FORCE_PREVIEW__ = true
            if (window.__FORCE_PREVIEW__) return true

            // Same cookie session that drives the cache-bypass/draft experience,
            // so the widget and the preview content stay in lockstep and persist
            // across navigation/reload. syncPreviewSession() reconciles ?preview
            // (enter) / ?preview=false (exit) into the cookie first.
            return Boolean(syncPreviewSession())
        }

        const isPreview = checkPreviewMode()
        setIsPreviewMode(isPreview)

        if (isPreview) {
            // Check if there's already a project selected in the URL
            const url = new URL(window.location.href)
            const currentProjectId = url.searchParams.get('optimizely_project')
            if (currentProjectId) {
                setSelectedProject(currentProjectId)
            }

            fetchCMSProjects()
        }
    }, [])

    // Watch for URL changes to keep selected project in sync
    useEffect(() => {
        if (typeof window === 'undefined') return

        const handleUrlChange = () => {
            const url = new URL(window.location.href)
            const currentProjectId = url.searchParams.get('optimizely_project')
            if (currentProjectId && currentProjectId !== selectedProject) {
                setSelectedProject(currentProjectId)
            } else if (!currentProjectId && selectedProject) {
                // If URL doesn't have project but component has one, clear selection
                setSelectedProject('')
            }
        }

        // Listen for popstate events (back/forward navigation)
        window.addEventListener('popstate', handleUrlChange)

        return () => {
            window.removeEventListener('popstate', handleUrlChange)
        }
    }, [selectedProject])

    // Handle click outside to close the widget
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (widgetRef.current && !widgetRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [isOpen])

    const getCMSApiUrl = () => {
        // Use the proxy endpoint to avoid CORS issues
        const preview = getPreviewSession()
        if (!preview) return `/api/cms-proxy/projects${siteQuery}`
        const sep = siteQuery ? '&' : '?'
        return `/api/cms-proxy/projects${siteQuery}${sep}preview=${encodeURIComponent(preview)}`
    }

    const fetchCMSProjects = async () => {
        setIsLoading(true)
        setError(null)

        try {
            const cmsApiUrl = getCMSApiUrl()
            const response = await fetch(cmsApiUrl)

            if (!response.ok) {
                throw new Error(`Failed to fetch projects: ${response.statusText}`)
            }

            const projects = await response.json()
            setAvailableProjects(projects)
        } catch (err) {
            setError(err.message)
            console.error('Error fetching CMS projects:', err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleProjectChange = (event) => {
        setSelectedProject(event.target.value)
    }

    const handleApply = async () => {
        if (typeof window === 'undefined') return

        // If no project selected, clear preview and reload
        if (!selectedProject) {
            clearPreviewData()
            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.delete('optimizely_project')
            toast({
                title: 'Preview Cleared',
                description: 'Preview mode deactivated. Page will reload.',
                status: 'info',
                duration: 2000,
                isClosable: true
            })
            setTimeout(() => {
                window.location.href = currentUrl.toString()
            }, 1000)
            return
        }

        setIsApplying(true)
        setError(null)

        try {
            // languageCode follows the active locale so the proxy fetches the
            // right language tree (not the hardcoded en-GB default); siteId
            // resolves the per-site CMS config.
            const blocksParams = new URLSearchParams()
            blocksParams.set('languageCode', getCurrentLocale())
            if (site?.id) blocksParams.set('siteId', site.id)
            const preview = getPreviewSession()
            if (preview) blocksParams.set('preview', preview)
            const fetchUrl = `/api/cms-proxy/projects/${selectedProject}/blocks?${blocksParams}`

            // Fetch the blocks for the selected project
            const response = await fetch(fetchUrl)

            if (!response.ok) {
                throw new Error(`Failed to fetch project blocks: ${response.status} ${response.statusText}`)
            }

            const blocksData = await response.json()

            // Check if we actually got any content (blocks or pages)
            const hasBlocks = blocksData.blocks && blocksData.blocks.length > 0
            const hasPages = blocksData.pages && blocksData.pages.length > 0
            if (!hasBlocks && !hasPages) {
                throw new Error(`No preview content found for project ${selectedProject}.`)
            }

            // Store the preview data in sessionStorage
            storePreviewData(blocksData, selectedProject)

            // Show success message
            const blockCount = blocksData.blocks ? blocksData.blocks.length : 0
            const pageCount = blocksData.pages ? blocksData.pages.length : 0
            toast({
                title: 'Preview Applied',
                description: `Preview mode activated with ${blockCount} blocks and ${pageCount} pages. Page will reload with preview content.`,
                status: 'success',
                duration: 3000,
                isClosable: true
            })

            // Add the project ID to the URL and reload
            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.set('optimizely_project', selectedProject)

            // Reload the page after a short delay to show the toast
            setTimeout(() => {
                window.location.href = currentUrl.toString()
            }, 1500)
        } catch (err) {
            setError(err.message)
            toast({
                title: 'Preview Failed',
                description: err.message,
                status: 'error',
                duration: 5000,
                isClosable: true
            })
        } finally {
            setIsApplying(false)
        }
    }

    if (!isPreviewMode) {
        return null
    }

    const position = cornerPosition(corner)
    const activeProjectName =
        selectedProject &&
        (availableProjects.find((p) => p.id.toString() === selectedProject)?.name || selectedProject)

    return (
        <Portal>
            {isOpen ? (
                <Box
                    ref={widgetRef}
                    position="fixed"
                    {...position}
                    zIndex={9999}
                    bg="white"
                    borderRadius="md"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    w={{base: 'calc(100vw - 32px)', sm: '300px'}}
                    maxW="calc(100vw - 32px)"
                >
                    <HStack
                        justify="space-between"
                        p={3}
                        borderBottom="1px solid"
                        borderColor="gray.200"
                        spacing={2}
                    >
                        <HStack spacing={2} minW={0}>
                            <Text fontSize="sm">📝</Text>
                            <VStack align="start" spacing={0} minW={0}>
                                <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.700"
                                    noOfLines={1}
                                >
                                    Optimizely Preview
                                </Text>
                                {activeProjectName && (
                                    <Text fontSize="xs" color="green.600" noOfLines={1}>
                                        Active: {activeProjectName}
                                    </Text>
                                )}
                            </VStack>
                        </HStack>
                        <HStack spacing={1}>
                            <Button
                                aria-label={`Move widget ${NEXT_DIRECTION[Math.max(0, CORNERS.indexOf(corner))]}`}
                                title={`Move ${NEXT_DIRECTION[Math.max(0, CORNERS.indexOf(corner))]}`}
                                size="xs"
                                variant="ghost"
                                minW="6"
                                px={0}
                                onClick={() => moveCorner(1)}
                            >
                                <MoveCornerIcon corner={corner} />
                            </Button>
                            <Button
                                aria-label="Minimize"
                                title="Minimize"
                                size="xs"
                                variant="ghost"
                                minW="6"
                                px={0}
                                color="gray.600"
                                fontSize="lg"
                                onClick={() => setIsOpen(false)}
                            >
                                –
                            </Button>
                        </HStack>
                    </HStack>

                    <VStack p={4} spacing={3} align="stretch">
                        {error && (
                            <Text fontSize="xs" color="red.500">
                                Error: {error}
                            </Text>
                        )}

                        <Box>
                            <Text fontSize="xs" mb={2} color="gray.600">
                                Select CMS Project:
                            </Text>
                            <Select
                                size="sm"
                                value={selectedProject}
                                onChange={handleProjectChange}
                                bg="gray.50"
                                isDisabled={isLoading}
                                placeholder={isLoading ? 'Loading projects...' : 'Published Version (no cache)'}
                            >
                                {availableProjects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name} ({project.createdBy})
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        <Button
                            size="sm"
                            colorScheme={selectedProject ? 'blue' : 'gray'}
                            onClick={handleApply}
                            isDisabled={isLoading || isApplying}
                            isLoading={isApplying}
                            loadingText="Applying..."
                        >
                            {selectedProject ? 'Apply Preview' : 'Clear Preview'}
                        </Button>
                    </VStack>
                </Box>
            ) : (
                <Button
                    aria-label="Open Optimizely preview"
                    title="Optimizely Preview"
                    position="fixed"
                    {...position}
                    zIndex={9999}
                    borderRadius="full"
                    boxSize="48px"
                    minW="48px"
                    p={0}
                    fontSize="2xl"
                    bg="white"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{bg: 'gray.50'}}
                    onClick={() => setIsOpen(true)}
                >
                    📝
                </Button>
            )}
        </Portal>
    )
}

export default PreviewModeProjectSelector
